// useAgentProfile hook — profile CRUD operations
import { useState, useEffect, useCallback } from "react";
import { api } from "@/services/api";
import type { AgentProfile, CreateAgentProfilePayload, UpdateAgentProfilePayload } from "@/types";

interface UseAgentProfileReturn {
  profile: AgentProfile | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createProfile: (payload: CreateAgentProfilePayload) => Promise<AgentProfile>;
  updateProfile: (payload: UpdateAgentProfilePayload) => Promise<AgentProfile>;
}

export function useAgentProfile(): UseAgentProfileReturn {
  const [profile, setProfile] = useState<AgentProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.get<AgentProfile>("/agent-profiles/me");
      setProfile(result);
    } catch (e: any) {
      if (e.status === 404) {
        setProfile(null);
      } else {
        setError(e.message || "Failed to load profile");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createProfile = async (payload: CreateAgentProfilePayload): Promise<AgentProfile> => {
    const result = await api.post<AgentProfile>("/agent-profiles", payload);
    setProfile(result);
    return result;
  };

  const updateProfile = async (payload: UpdateAgentProfilePayload): Promise<AgentProfile> => {
    const result = await api.patch<AgentProfile>("/agent-profiles/me", payload);
    setProfile(result);
    return result;
  };

  return { profile, isLoading, error, refresh, createProfile, updateProfile };
}
